import React from 'react';
import { cloneDeep, get, find, set } from 'lodash';
import { withRouter } from 'react-router-dom';

// local dependencies
import { callListWidget as CallListWidgetView } from 'ui/views/widgets/callListWidget';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS } from 'constants/common';
import { CallListWidgetService } from 'services/callListWidget';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import queryString from 'query-string';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as errorOverlay from 'utils/errorOverlay';

import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallListWidget } from 'ui/containers/CallListWidget';
 *
 * // render
 * <CallListWidget propName='propValue'/>
 */
export class _CallListWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );
        this.callMarkResolved = this.callMarkResolved.bind( this );

        // create service instance
        this.callListWidgetService = new CallListWidgetService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'CallListWidget.fetchComponentData()' );

        // cancel ongoing request
        this.callListWidgetService.cancel();

        // fetch component data
        const { status, data } = await this.callListWidgetService.fetchComponentData(  {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    async callMarkResolved( { url, callData, rowData, config }, callback ) {
        log.debug( 'CallListWidget.callMarkResolved()' );
        
        // cancel ongoing request
        this.callListWidgetService.cancel();

         // preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.callListWidgetService.fetchMarkResolvedData(  {
            url: url,
            data: {}
        } );

        // preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                
                // networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CALL_MARK_RESOLVED,
                    showCloseButton: false,
                    ignoreOutsideClick: false
                },
                config: {},
                data: {
                    ...data,
                    onSumbitClick: async ( { values } ) => {

                         // preloader
                        pageLevelPreloader.show();

                        // cancel ongoing request
                        this.callListWidgetService.cancel();

                        // fetch component data
                        const { _status } = await this.callListWidgetService.markResolvedData( {
                            url: config.url,
                            data: {
                                context: {},
                                data: {
                                    ...callData,
                                    ...values
                                }
                            }
                        } );

                        // preloader
                        pageLevelPreloader.hide();

                        // close overlay
                        overlay.close();

                        if( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            log.debug( this.state );
                            const callResolutionOptions = get( this.state, 'componentData.data.options.callResolution', [] );
                            const callResolved = find( callResolutionOptions, { name: 'resolved' } );

                            const item = cloneDeep( rowData );
                            
                            set( item, 'actionsVisibility.groupActions.markResolved', false );
                            set( item, 'values.resolution', callResolved.value );

                            callback( { rowData: item } );
                            
                            successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ), } );

                        } else {

                            errorOverlay.open();

                        }

                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );

        }
    }

    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {} } = {}, callback ) {
        log.debug( 'CallListWidget.handleActionClick()' ); // @TODO: remove

        if( 'column' === type ) {
            this.props.history.push( { pathname: DASHBOARD_SUB_ROUTES.CALL_DETAIL, search: queryString.stringify( {
                callId: item.id,
            }  ) } );
        }

        if( 'groupAction' === type ) {

            this.callMarkResolved( {
                url: value.url,
                callData: {
                    callId: item.id
                },
                rowData: item,
                config: value.config
            }, callback );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.callListWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CallListWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_CallListWidget.displayName = '_CallListWidget';

// set default props
_CallListWidget.defaultProps = {
    widget: {}, // widget configuration
};

// prop types
_CallListWidget.propTypes = {};

export const CallListWidget = withRouter( _CallListWidget );
